/**
 * Authentication Provider
 * Manages global authentication state using React Context
 */

'use client';
import * as Sentry from '@sentry/nextjs';
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { events } from '@Events';
import { getAnonymousId, setUserId, resetIdentity, endSessionOnLogout } from '@/events/core/enricher';
import { getAuthApiService } from '../services/authApi';
import { 
  AuthState, 
  AuthContextType, 
  AuthError,
  User,
  AuthTokens,
  AttributionPayload,
  TelegramAuthData
} from '../types';
import { tokenStorage, setupStorageListener, decodeUserFromAccessToken } from '../storage';
import { getGoogleAuthService } from '../services/googleAuth';
import { useClientSideAuth } from '../hooks/useClientSideAuth';
import { getFirstTouchAttribution } from '../attribution';

/**
 * WE-115: resolves identity for the analytics layer — called on every
 * successful login/sign_up AND on session restore (page reload), so
 * `getEventContext().user_id` is populated for the vast majority of real
 * traffic (restored sessions), not just fresh logins.
 *
 * `setUserId` is synchronous/idempotent (safe to call every restore); the
 * `identify` emit itself is deduped downstream by the gtag adapter when the
 * same user_id is already known, so calling this on every restore does not
 * produce duplicate dataLayer pushes.
 */
function emitIdentify(user: Partial<User>, isNewUser = false): void {
  if (!user.id) return;
  setUserId(user.id);
  events.emit('identify', {
    anonymous_id: getAnonymousId(),
    user_id: user.id,
    email: user.email ?? null,
    name: user.name ?? null,
    is_new_user: isNewUser,
  });
}

/**
 * WE-115 Wave 1: `is_new_user` now comes from legolas (all 5 auth endpoints,
 * see WE-115-backend-is_new_user-plan.md) — a brand-new registration emits
 * `sign_up` + `trial_started` instead of `login`. The trial itself starts
 * automatically server-side (create_trial_subscription) at the same moment,
 * not from a UI button — there is no "start trial" click to hang this on.
 */
function emitLoginOutcome(
  method: string,
  user: Partial<User>,
  isNewUser: boolean,
  attribution?: AttributionPayload
): void {
  // Identify FIRST (Segment/Mixpanel convention): setUserId must run before
  // the conversion events below reach the destination adapters, or
  // sign_up/trial_started/login are pushed with user_id null and empty
  // user_data — degrading match quality (GA4 enhanced conversions, Meta
  // advanced matching) on the most valuable events.
  emitIdentify(user, isNewUser);
  if (isNewUser) {
    events.emit('sign_up', { method });
    events.emit('trial_started', {
      utm_source: attribution?.utm_source,
      utm_campaign: attribution?.utm_campaign,
    });
  } else {
    events.emit('login', { method });
  }
}

/**
 * Authentication actions
 */
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: { user: Partial<User>; tokens: AuthTokens } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_TOKENS'; payload: AuthTokens }
  | { type: 'CLEAR_ERROR' };

/**
 * Initial authentication state
 */
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to check stored tokens
  error: null,
};

/**
 * Authentication reducer
 */
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case 'UPDATE_TOKENS':
      return {
        ...state,
        tokens: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Authentication provider props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication provider component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { canAccessAuth } = useClientSideAuth();
  
  // Note: getGoogleAuthService() is called lazily inside login() — it throws
  // when NEXT_PUBLIC_GOOGLE_CLIENT_ID is absent, and the envless build (WE-506)
  // has no env at prerender time. Creating it during render would crash the
  // build-time prerender of static pages.
  const authApiService = getAuthApiService();

  /**
   * Initialize authentication state from storage
   */
  const initializeAuth = useCallback(async () => {
    try {
      const storedTokens = tokenStorage.getTokens();
      let storedUser  = tokenStorage.getUser();

      if (storedTokens) {
        // Check if token is potentially expired
        if (tokenStorage.isTokenPotentiallyExpired()) {
          try {
            // Try to refresh token
            console.log('Info [InitializeAuth]: Try to refresh token');
            const newTokens = await authApiService.refreshToken(storedTokens.refresh_token);
            tokenStorage.setTokens(newTokens);
            storedUser = decodeUserFromAccessToken(newTokens.access_token);
            tokenStorage.setUser(storedUser);

            // WE-115: resolve identity on session restore too, not just on
            // fresh login — otherwise every event for a returning user
            // (the majority of real traffic) carries user_id: null.
            emitIdentify(storedUser);

            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: storedUser,
                tokens: newTokens,
              },
            });
          } catch (error) {
            // Refresh failed, clear stored data
            console.log('Error [Refresh Token]', error);
            tokenStorage.clearAll();
            dispatch({ type: 'LOGOUT' });
          }
        } else {
          // Token seems valid, restore state
          if(!storedUser){ // Double check if storedUser cookies are expired or deleted
            storedUser = decodeUserFromAccessToken(storedTokens.access_token);
            tokenStorage.setUser(storedUser);
          }
          // WE-115: same as above — resolve identity on restore.
          emitIdentify(storedUser);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: storedUser,
              tokens: storedTokens,
            },
          });
        }
      } else {
        // No stored tokens, user is not authenticated
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      tokenStorage.clearAll();
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [authApiService]);

  /**
   * Login with Google token
   */
  const login = useCallback(async (googleToken: string, attribution?: AttributionPayload) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      // Parse Google token for user info (client-side only)
      const googlePayload = getGoogleAuthService().parseIdToken(googleToken);
      
      // Login with backend (backend will verify the token)
      const firstTouchAttribution = attribution ?? getFirstTouchAttribution();
      const loginResponse = await authApiService.loginWithGoogle(googleToken, firstTouchAttribution);

      // Create user object from Google payload
      const user: User = {
        id: googlePayload.sub,
        email: googlePayload.email,
        name: googlePayload.name,
        picture: googlePayload.picture,
        verified_email: googlePayload.email_verified,
      };

      // Store tokens and user data
      tokenStorage.setTokens(loginResponse);
      tokenStorage.setUser(user);

      emitLoginOutcome('google', user, loginResponse.is_new_user ?? false, firstTouchAttribution);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          tokens: loginResponse,
        },
      });
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Login failed. Please try again.';

      // Send login failed event
      events.emit('login_failed', { method: 'google', reason: message });
      Sentry.captureException(new Error('Login failed'), {
                level: 'warning',
                tags: {
                    category: 'login',
                    type: 'login_failed',
                },
                extra: {
                    error: error,
                },
            });

      dispatch({ type: 'SET_ERROR', payload: message });
      tokenStorage.clearAll();
  }
  }, [authApiService]);

  /**
   * Login with email and password
   */
  const loginWithPassword = useCallback(async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const loginResponse = await authApiService.loginWithPassword(email, password);
      const user = decodeUserFromAccessToken(loginResponse.access_token);

      tokenStorage.setTokens(loginResponse);
      tokenStorage.setUser(user);

      // password login never registers — no attribution to attach
      emitLoginOutcome('password', user, loginResponse.is_new_user ?? false);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens: loginResponse },
      });
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Login failed. Please check your credentials.';

      events.emit('login_failed', { method: 'password', reason: message });
      Sentry.captureException(new Error('Password login failed'), {
        level: 'warning',
        tags: { category: 'login', type: 'password_login_failed' },
        extra: { error },
      });

      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [authApiService]);

  /**
   * Login with magic link token (after user clicks the link in email)
   */
  const loginWithMagicLinkToken = useCallback(async (token: string, attribution?: AttributionPayload) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const firstTouchAttribution = attribution ?? getFirstTouchAttribution();
      const loginResponse = await authApiService.verifyMagicLink(token, firstTouchAttribution);
      const user = decodeUserFromAccessToken(loginResponse.access_token);

      tokenStorage.setTokens(loginResponse);
      tokenStorage.setUser(user);

      emitLoginOutcome('magic_link', user, loginResponse.is_new_user ?? false, firstTouchAttribution);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens: loginResponse },
      });
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Magic link verification failed. Please try again.';

      events.emit('login_failed', { method: 'magic_link', reason: message });
      Sentry.captureException(new Error('Magic link login failed'), {
        level: 'warning',
        tags: { category: 'login', type: 'magic_link_failed' },
        extra: { error },
      });

      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [authApiService]);

  /**
   * Login with Amazon access token
   */
  const loginWithAmazon = useCallback(async (accessToken: string, attribution?: AttributionPayload) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const firstTouchAttribution = attribution ?? getFirstTouchAttribution();
      const loginResponse = await authApiService.loginWithAmazon(accessToken, firstTouchAttribution);
      const user = decodeUserFromAccessToken(loginResponse.access_token);

      tokenStorage.setTokens(loginResponse);
      tokenStorage.setUser(user);

      emitLoginOutcome('amazon', user, loginResponse.is_new_user ?? false, firstTouchAttribution);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens: loginResponse },
      });
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Amazon login failed. Please try again.';

      events.emit('login_failed', { method: 'amazon', reason: message });
      Sentry.captureException(new Error('Amazon login failed'), {
        level: 'warning',
        tags: { category: 'login', type: 'amazon_login_failed' },
        extra: { error },
      });

      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [authApiService]);

  /**
   * Login with Telegram auth data
   */
  const loginWithTelegram = useCallback(async (data: TelegramAuthData, attribution?: AttributionPayload) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const firstTouchAttribution = attribution ?? getFirstTouchAttribution();
      const telegramData = { ...data, attribution: firstTouchAttribution };
      const loginResponse = await authApiService.loginWithTelegram(telegramData);
      const user = decodeUserFromAccessToken(loginResponse.access_token);

      tokenStorage.setTokens(loginResponse);
      tokenStorage.setUser(user);

      emitLoginOutcome('telegram', user, loginResponse.is_new_user ?? false, firstTouchAttribution);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens: loginResponse },
      });
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Telegram login failed. Please try again.';

      events.emit('login_failed', { method: 'telegram', reason: message });
      Sentry.captureException(new Error('Telegram login failed'), {
        level: 'warning',
        tags: { category: 'login', type: 'telegram_login_failed' },
        extra: { error },
      });

      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [authApiService]);

  /**
   * Send magic link to email
   */
  const sendMagicLink = useCallback(async (email: string, attribution?: AttributionPayload) => {
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const firstTouchAttribution = attribution ?? getFirstTouchAttribution();
      await authApiService.sendMagicLink(email, firstTouchAttribution);
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Failed to send magic link. Please try again.';

      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  }, [authApiService]);

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (email: string) => {
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      await authApiService.requestPasswordReset(email);
    } catch (error) {
      const message = error instanceof AuthError
        ? error.message
        : 'Failed to send reset link. Please try again.';

      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  }, [authApiService]);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // Try to logout from backend if we have a token
      // if (state.tokens?.access_token) {
      //   await authApiService.logout(state.tokens.access_token);
      // }
    } catch (error) {
      // Log but don't throw - logout should always succeed locally
      console.warn('Backend logout failed:', error);
    } finally {
      // Always clear local storage and state
      tokenStorage.clearAll();
      dispatch({ type: 'LOGOUT' });

      // Send logout event — Redux store reset is handled by logoutMiddleware
      events.emit('logout');
      // WE-115: reset identity AFTER the logout event itself (so it's still
      // attributed to the outgoing user), so the NEXT events on this tab
      // (before any subsequent login) never inherit stale user_id/anonymous_id.
      endSessionOnLogout();
      resetIdentity();
    }
  }, [state.tokens?.access_token, authApiService]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Initialize authentication on mount (only after client-side is ready)
   */
  useEffect(() => {
    if (canAccessAuth) {
      initializeAuth();
    }
  }, [initializeAuth, canAccessAuth]);

  /**
   * Set up storage listener for cross-tab synchronization (only after client-side is ready)
   */
  useEffect(() => {
    if (!canAccessAuth) return;

    const cleanup = setupStorageListener(() => {
      // Re-initialize auth when storage changes in other tabs
      initializeAuth();
    });

    return cleanup;
  }, [initializeAuth, canAccessAuth]);

  /**
   * Context value
   */
  const contextValue: AuthContextType = {
    ...state,
    login,
    loginWithPassword,
    loginWithMagicLinkToken,
    loginWithAmazon,
    loginWithTelegram,
    sendMagicLink,
    requestPasswordReset,
    logout,
    clearError,
  };

  return (
      <AuthContext.Provider value={contextValue}>
          {children}
      </AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

/**
 * Hook to get authentication token for API calls
 */
export function useAuthToken(): string | null {
  const { tokens } = useAuth();
  return tokens?.access_token || null;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}
